import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from "recharts";
import {
  MapPin, Users, Truck, ClipboardList, Plus, ChevronRight,
  CheckCircle, XCircle, ArrowLeft, Calendar, TrendingUp, Download, FileSpreadsheet
} from "lucide-react";
import { toast } from "sonner";

const SHIFTS = { "MAÑANA": "bg-amber-50 text-amber-700", "TARDE": "bg-orange-50 text-orange-700", "NOCHE": "bg-slate-100 text-slate-600" };

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [contract, setContract] = useState(null);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState("resumen");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authAxios.get(`/contracts/${id}`),
      authAxios.get(`/contracts/${id}/stats`),
      authAxios.get(`/work-reports?contract_id=${id}&limit=20`),
      authAxios.get(`/personnel?contract_id=${id}&active=true`),
      authAxios.get(`/vehicles?contract_id=${id}`)
    ]).then(([c, s, r, p, v]) => {
      setContract(c.data);
      setStats(s.data);
      setReports(r.data);
      setPersonnel(p.data);
      setVehicles(v.data);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!contract) return <div className="p-6">Contrato no encontrado</div>;

  const color = contract.color || "#007934";
  const chartData = stats?.recent_reports?.slice().reverse().map(r => ({
    date: r.date?.slice(5),
    day: r.day_of_week?.slice(0, 3),
    cumplimiento: r.completion_rate,
    personal: r.total_staff,
    vehiculos: r.total_vehicles
  })) || [];

  const tabs = [
    { id: "resumen", label: "Resumen" },
    { id: "partes", label: `Partes (${reports.length})` },
    { id: "personal", label: `Personal (${personnel.length})` },
    { id: "maquinaria", label: `Maquinaria (${vehicles.length})` },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="animate-fade-in-up">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-4 transition-colors">
          <ArrowLeft size={16} /> Volver al dashboard
        </button>
        <div className="card-white p-6 border-l-4" style={{ borderLeftColor: color }}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{contract.code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${contract.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                  {contract.status === "active" ? "Activo" : "Inactivo"}
                </span>
              </div>
              <h1 className="text-3xl font-black text-slate-900">{contract.name}</h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><MapPin size={13} />{contract.city}, {contract.region}</span>
                <span className="flex items-center gap-1"><Calendar size={13} />{contract.start_date} — {contract.end_date}</span>
              </div>
              <p className="text-slate-500 text-sm mt-2">{contract.description}</p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="new-report-contract-btn"
                onClick={() => navigate("/work-reports/new", { state: { contractId: id } })}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md transition-all active:scale-95"
                style={{ backgroundColor: color }}
              >
                <Plus size={15} /> Nuevo Parte
              </button>
              <button
                data-testid="export-contract-btn"
                onClick={async () => {
                  try {
                    const BACKEND = process.env.REACT_APP_BACKEND_URL;
                    const token = localStorage.getItem("vz_token");
                    const r = await fetch(`${BACKEND}/api/work-reports/export?contract_id=${id}&format=xlsx`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!r.ok) throw new Error();
                    const blob = await r.blob();
                    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
                    a.download = `Partes_${id}.xlsx`; a.click();
                    toast.success("Excel descargado");
                  } catch { toast.error("Error al exportar"); }
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all active:scale-95"
              >
                <FileSpreadsheet size={14} className="text-emerald-600" /> Excel
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
            {[
              { label: "Personal Activo", value: stats?.personnel_count, icon: Users },
              { label: "Vehículos Activos", value: stats?.vehicle_count, icon: Truck },
              { label: "Cumplimiento Medio", value: `${stats?.avg_completion_rate}%`, icon: TrendingUp },
              { label: "En Mantenimiento", value: stats?.vehicle_maintenance, icon: Truck },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="text-center">
                <p className="text-2xl font-black text-slate-800">{value ?? "—"}</p>
                <p className="text-xs text-slate-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services */}
      {contract.services?.length > 0 && (
        <div className="flex flex-wrap gap-2 animate-fade-in-up">
          {contract.services.map(s => (
            <span key={s} className="text-xs px-3 py-1.5 rounded-full font-medium bg-[#E6F4EA] text-[#007934]">{s}</span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="animate-fade-in-up">
        <div className="flex border-b border-slate-200 gap-0">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all -mb-px ${
                activeTab === t.id
                  ? "border-[#007934] text-[#007934]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Resumen tab */}
        {activeTab === "resumen" && (
          <div className="pt-5 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card-white p-5">
                <h3 className="font-bold text-slate-700 mb-4 text-sm">Tendencia de Cumplimiento (14 días)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} unit="%" />
                    <Tooltip formatter={(v) => [`${v}%`, "Cumplimiento"]} />
                    <Line type="monotone" dataKey="cumplimiento" stroke={color} strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="card-white p-5">
                <h3 className="font-bold text-slate-700 mb-4 text-sm">Personal y Vehículos Diarios</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barSize={12}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <Tooltip />
                    <Bar dataKey="personal" name="Personal" fill={color} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="vehiculos" name="Vehículos" fill={color + "66"} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Partes tab */}
        {activeTab === "partes" && (
          <div className="pt-4 space-y-3">
            {reports.map(r => (
              <div
                key={r.id}
                data-testid={`report-item-${r.id}`}
                onClick={() => navigate(`/work-reports/${r.id}`)}
                className="card-white p-4 cursor-pointer hover:shadow-md transition-all flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "18" }}>
                  <span className="text-xs font-bold" style={{ color }}>{r.date?.slice(8, 10)}</span>
                  <span className="text-[10px] text-slate-400">{r.date?.slice(5, 7)}/{r.date?.slice(0, 4)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">{r.day_of_week}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.uploaded_from === "excel" ? "bg-blue-50 text-blue-600" : r.uploaded_from === "ai" ? "bg-purple-50 text-purple-600" : "bg-slate-100 text-slate-500"}`}>
                      {r.uploaded_from === "excel" ? "Excel" : r.uploaded_from === "ai" ? "IA" : "Manual"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{r.total_staff} operarios · {r.total_vehicles} vehículos · {r.entries?.length || 0} entradas</p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-black ${r.completion_rate >= 95 ? "text-emerald-600" : r.completion_rate >= 80 ? "text-amber-500" : "text-red-500"}`}>
                    {r.completion_rate}%
                  </p>
                  <p className="text-xs text-slate-400">cumplimiento</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* Personal tab */}
        {activeTab === "personal" && (
          <div className="pt-4">
            <table className="w-full data-table card-white overflow-hidden rounded-xl">
              <thead><tr><th>Nombre</th><th>Categoría</th><th>Rol</th><th>Centro de Trabajo</th><th>Días</th><th>Estado</th></tr></thead>
              <tbody>
                {personnel.map(p => (
                  <tr key={p.id} data-testid={`personnel-row-${p.id}`}>
                    <td className="font-medium text-slate-800">{p.full_name}</td>
                    <td><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{p.category}</span></td>
                    <td className="text-slate-500 text-sm">{p.role}</td>
                    <td className="text-slate-500 text-sm">{p.work_center}</td>
                    <td className="text-xs text-slate-400">{(p.schedule_days || []).join("-")}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{p.active ? "Activo" : "Inactivo"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Maquinaria tab */}
        {activeTab === "maquinaria" && (
          <div className="pt-4">
            <table className="w-full data-table card-white overflow-hidden rounded-xl">
              <thead><tr><th>Matrícula</th><th>Marca</th><th>Modelo</th><th>Familia</th><th>Combustible</th><th>Estado</th></tr></thead>
              <tbody>
                {vehicles.map(v => (
                  <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                    <td className="font-mono font-bold text-slate-700">{v.plate}</td>
                    <td className="text-slate-600">{v.brand}</td>
                    <td className="text-slate-500 text-sm">{v.model_name}</td>
                    <td><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{v.family}</span></td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v.fuel_type === "Eléctrico" ? "bg-emerald-50 text-emerald-600" : v.fuel_type === "GNC" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                        {v.fuel_type}
                      </span>
                    </td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                        {v.status === "active" ? "Activo" : "Mantenimiento"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
